import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { BootstrapConfig, FeatureFlag, FormSchema, Plan } from '@solote/contracts';
import { fetchBootstrap } from './bootstrap-api';
import { applyTheme } from './theme';

/**
 * PROVEDOR DE CONFIGURACAO
 *
 * Uma unica requisicao (`/bootstrap`) entrega marca, tema, textos, menus,
 * secoes da home, planos, regras, filtros e formularios. Todo componente do
 * app le daqui — nao existe constante de negocio importada de arquivo `.ts`.
 *
 * A resposta e cacheada pelo ETag; o painel, ao publicar, invalida a chave no
 * edge e o proximo carregamento ja traz a nova versao.
 */

type ConfigContextValue = {
  config: BootstrapConfig;
};

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { data: config } = useSuspenseQuery({
    queryKey: ['bootstrap'],
    queryFn: fetchBootstrap,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    applyTheme(config.site.theme);
    document.documentElement.lang = 'pt-BR';
    if (config.site.brand.faviconUrl) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? document.createElement('link');
      link.rel = 'icon';
      link.href = config.site.brand.faviconUrl;
      document.head.appendChild(link);
    }
  }, [config]);

  const value = useMemo(() => ({ config }), [config]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): BootstrapConfig {
  const context = useContext(ConfigContext);
  if (!context) throw new Error('useConfig precisa estar dentro de <ConfigProvider>.');
  return context.config;
}

export function useSiteConfig() {
  return useConfig().site;
}

export function useBrand() {
  return useConfig().site.brand;
}

/**
 * Resolve um texto pela chave. Nenhum literal de interface deve existir em
 * componente: `t('listing.contact.cta')` e sempre reescrivel pelo painel.
 * `fallback` cobre a chave ainda nao cadastrada — em dev, avisa no console.
 */
export function useText() {
  const texts = useConfig().site.texts;

  return useMemo(() => {
    return (key: string, params?: Record<string, string | number>, fallback?: string): string => {
      const template = texts[key] ?? fallback;
      if (template === undefined) {
        if (import.meta.env.DEV) console.warn(`[texts] chave sem traducao: "${key}"`);
        return key;
      }
      if (!params) return template;
      return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
        const value = params[name];
        return value === undefined ? match : String(value);
      });
    };
  }, [texts]);
}

function isFlagOn(flag: FeatureFlag | undefined, stableId: string): boolean {
  if (!flag || !flag.enabled) return false;
  if (flag.rolloutPercentage >= 100) return true;
  if (flag.rolloutPercentage <= 0) return false;
  // Bucket estavel por usuario/sessao: o mesmo visitante nao ve a feature
  // piscar entre navegacoes.
  let hash = 0;
  const seed = `${flag.key}:${stableId}`;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return hash % 100 < flag.rolloutPercentage;
}

export function useFeature(key: string, stableId = 'anonymous'): boolean {
  const features = useConfig().site.features;
  return useMemo(() => isFlagOn(features.find((flag) => flag.key === key), stableId), [features, key, stableId]);
}

/** Formularios publicados. Retorna `null` se a chave nao existe ou esta off. */
export function useFormSchema(key: string): FormSchema | null {
  const forms = useConfig().forms;
  const schema = forms[key];
  if (!schema || !schema.publication.enabled) return null;
  return schema;
}

export function usePlans(): Plan[] {
  const commerce = useConfig().commerce;
  return useMemo(
    () => commerce.plans.filter((plan) => plan.publication.enabled).sort((a, b) => a.publication.order - b.publication.order),
    [commerce.plans],
  );
}

export function usePlan(slug: string | null | undefined): Plan | null {
  const plans = usePlans();
  if (!slug) return null;
  return plans.find((plan) => plan.slug === slug) ?? null;
}
